import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, X } from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import type { ErpPolicySuggestionClass } from '@siliconx/shared';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';

/**
 * SIL-271 (W4-T11, spec decision 8) — post-promote policy-suggestion
 * banner. Consumes `GET /api/client/financials/policy/suggestions`
 * (SIL-269, W3-T9, read-only dominant-method/life aggregation). "Apply"
 * calls the EXISTING class-override policy endpoint
 * (`PUT /api/client/financials/policy/class/:assetClassId`) — there is no
 * separate apply-suggestion write path by design (spec 3.2: "no new
 * server-side apply-suggestion write path").
 *
 * Admin-only (mirrors the rest of the Capitalization Policy screen's write
 * controls) — members never see an Apply button, matching
 * routes/financials/policy.tsx's `isAdmin` gate.
 */
export function PolicySuggestionBanner({
  isAdmin,
  onApplied,
}: {
  isAdmin: boolean;
  onApplied: () => void;
}) {
  const queryClient = useQueryClient();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const suggestionsQ = useQuery({
    queryKey: qk.financials.policySuggestions(),
    queryFn: async () => {
      const res = await api.api.client.financials.policy.suggestions.$get();
      await throwIfNotOk(res);
      return res.json();
    },
  });

  const disagreeing = (suggestionsQ.data?.classes ?? []).filter(
    (c) => (c.disagreesOnMethod || c.disagreesOnLife) && !dismissedIds.has(c.assetClassId),
  );

  if (!isAdmin || suggestionsQ.isLoading || suggestionsQ.error || disagreeing.length === 0) {
    return null;
  }

  async function apply(row: ErpPolicySuggestionClass) {
    setApplyingId(row.assetClassId);
    setError(null);
    try {
      // Write DTO only accepts the phase-1 method pair (straight_line /
      // declining_balance, spec §2.2 — sum_of_years_digits is read-only).
      // A `sum_of_years_digits`/`other` dominant bucket still shows the
      // banner (life can still disagree independently) but falls back to
      // the currently-configured method rather than writing an invalid one.
      const canApplyMethod =
        row.disagreesOnMethod &&
        (row.dominantErpMethodBucket === 'straight_line' || row.dominantErpMethodBucket === 'declining_balance');
      const method = canApplyMethod
        ? (row.dominantErpMethodBucket as 'straight_line' | 'declining_balance')
        : row.configuredMethod === 'declining_balance'
          ? 'declining_balance'
          : 'straight_line';
      const usefulLifeYears =
        row.disagreesOnLife && row.dominantErpLifeMonths
          ? Math.max(1, Math.round(row.dominantErpLifeMonths / 12))
          : row.configuredUsefulLifeYears ?? 5;

      const res = await api.api.client.financials.policy.class[':assetClassId'].$put({
        param: { assetClassId: row.assetClassId },
        json: {
          method,
          usefulLifeYears,
          dbFactor: method === 'declining_balance' ? 2 : null,
          salvagePct: 0,
          capThresholdUsd: 0,
        },
      });
      await throwIfNotOk(res);
      setDismissedIds((prev) => new Set(prev).add(row.assetClassId));
      queryClient.invalidateQueries({ queryKey: qk.financials.policySuggestions() });
      onApplied();
    } catch (ex: unknown) {
      setError((ex as Error).message);
    } finally {
      setApplyingId(null);
    }
  }

  return (
    <div className="space-y-2">
      {disagreeing.map((row) => (
        <div
          key={row.assetClassId}
          className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <div className="flex flex-1 items-start gap-3">
            <Sparkles className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <div className="text-sm text-amber-800">
              <span className="font-medium">{row.assetClassName}</span> — your ERP register reports{' '}
              {row.disagreesOnMethod && row.dominantErpMethodBucket && (
                <>
                  <span className="font-medium">{formatMethodBucket(row.dominantErpMethodBucket)}</span>{' '}
                </>
              )}
              {row.disagreesOnLife && row.dominantErpLifeMonths && (
                <>
                  ({Math.round(row.dominantErpLifeMonths / 12)}-year useful life){' '}
                </>
              )}
              across {row.sampleSize} imported asset{row.sampleSize === 1 ? '' : 's'}, which differs from
              your configured policy ({row.configuredPolicyTier.replace('_', ' ')}). Apply the ERP-reported
              method/life as a class override?
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              disabled={applyingId === row.assetClassId}
              onClick={() => void apply(row)}
              className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {applyingId === row.assetClassId ? 'Applying…' : 'Apply suggestion'}
            </button>
            <button
              type="button"
              aria-label="Dismiss suggestion"
              onClick={() => setDismissedIds((prev) => new Set(prev).add(row.assetClassId))}
              className={cn('shrink-0 rounded-md p-1 text-amber-600 hover:bg-amber-100')}
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
      ))}
      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
}

function formatMethodBucket(bucket: ErpPolicySuggestionClass['dominantErpMethodBucket']): string {
  switch (bucket) {
    case 'straight_line':
      return 'straight-line';
    case 'declining_balance':
      return 'declining-balance';
    case 'sum_of_years_digits':
      return 'sum-of-years-digits';
    default:
      return 'a different method';
  }
}
